import AreaDrawingMap from "@/components/Map/AreaDrawingMap";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SubmitButton } from "@/components/ui/submit-button";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { errorHandler } from "@/lib/utils";
import { geofenceService } from "@/services/api/geofence";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm, type Resolver } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { z } from "zod";
import GeofenceActions from "./GeofenceActions";

const actionFormSchema = z.object({
  vehicle_ids: z.array(z.number()).min(1),
  vehicles: z.array(z.object({ id: z.number(), label: z.string() })).optional(),
  action: z.number(),
  entry_exit: z.number(),
  log: z.string().optional(),
  action_id: z.number().optional(),
});

const GeofenceFormSchema = z.object({
  name: z.string().min(1, "O nome é obrigatório"),
  max_speed: z.coerce
    .number()
    .min(0, "A velocidade limite deve ser maior ou igual a 0")
    .optional(),
  actions: z.array(actionFormSchema).optional(),
  tempVehicleIds: z.array(z.number()).optional(),
  tempVehicles: z
    .array(z.object({ id: z.number(), label: z.string() }))
    .optional(),
  tempEntryExit: z.number().optional(),
  tempAction: z.number().optional(),
  tempLog: z.string().optional(),
});

export type GeofenceFormValues = z.infer<typeof GeofenceFormSchema>;

const GeofenceForm = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [polygons, setPolygons] = useState<number[][][]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<GeofenceFormValues>({
    // ponytail: resolvers 5 types the raw input apart from the parsed
    // output; the form keeps the parsed type, as it did on resolvers 3.
    resolver: zodResolver(GeofenceFormSchema) as Resolver<GeofenceFormValues>,
    defaultValues: {
      name: "",
      max_speed: undefined,
      actions: [],
      tempVehicleIds: [],
      tempVehicles: [],
      tempEntryExit: 0,
      tempAction: 2,
      tempLog: "",
    },
  });

  const { handleSubmit, loading } = useFormSubmit({
    id,
    createService: geofenceService.create,
    updateService: geofenceService.update,
    onSuccess: () => {
      navigate("/geofences");
    },
  });

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      geofenceService
        .show(Number(id))
        .then((response) => {
          const geofence = response.data;

          form.setValue("name", geofence.title);
          form.setValue("max_speed", geofence.max_speed || undefined);

          if (geofence.coordinates) {
            const coordsArray = parseCoordinatesString(geofence.coordinates);
            setPolygons([coordsArray]);
          }

          if (geofence.actions && geofence.actions.length > 0) {
            const formattedActions = geofence.actions.map((action) => ({
              vehicle_ids: [action.vehicle_id],
              vehicles: [
                {
                  id: action.vehicle_id,
                  label: `${action.vehicle_plate || ""} - ${action.vehicle_title || ""}`,
                },
              ],
              action: action.action,
              entry_exit: action.entry_exit,
              log: action.log || "",
              action_id: action.id,
            }));
            form.setValue("actions", formattedActions);
          }
        })
        .catch((err) => {
          errorHandler(err, "Erro ao carregar dados da cerca eletrônica.");
        })
        .finally(() => setIsLoading(false));
    }
  }, [id, form]);

  const parseCoordinatesString = (coordsStr: string): number[][] => {
    return coordsStr.split(";").map((point) => {
      const [lat, lng] = point.split(",").map(Number);
      return [lat, lng];
    });
  };

  const getCoordinatesString = (polygonCoords: number[][][]): string => {
    if (polygonCoords.length === 0 || polygonCoords[0].length === 0) return "";
    return polygonCoords[0].map((coord) => `${coord[0]},${coord[1]}`).join(";");
  };

  const onSubmit = async (data: GeofenceFormValues) => {
    if (polygons.length === 0) {
      toast.error("Por favor, desenhe a área da cerca eletrônica no mapa.");
      return;
    }

    try {
      setIsLoading(true);
      const coordinatesString = getCoordinatesString(polygons);

      const payload = {
        name: data.name,
        coordinates: coordinatesString,
        max_speed: data.max_speed,
        actions: data.actions?.map((action) => ({
          action_id: action.action_id,
          vehicle_ids: action.vehicle_ids,
          action: action.action,
          entry_exit: action.entry_exit,
          log: action.log,
        })),
      };

      await handleSubmit(payload);
    } catch (err) {
      errorHandler(err, "Erro ao salvar cerca eletrônica");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="mx-auto">
      <CardHeader>
        <CardTitle>
          {id ? "Editar Cerca Eletrônica" : "Cadastrar Cerca Eletrônica"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <div className="flex flex-col md:grid md:grid-cols-3 gap-3 h-full w-full">
              <div className="w-full md:col-span-1 p-4 space-y-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Título da cerca eletrônica</FormLabel>
                      <FormControl>
                        <Input placeholder="Digite o título" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="max_speed"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Velocidade limite (km/h)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          placeholder="Digite a velocidade limite"
                          value={field.value ?? ""}
                          onChange={(event) => {
                            const value = event.target.value;
                            field.onChange(
                              value === "" ? undefined : Number(value),
                            );
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <GeofenceActions form={form} />

                <div className="flex flex-col space-y-2 pt-8 border-t sm:flex-row sm:space-x-2 sm:space-y-0">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/geofences")}
                  >
                    Cancelar
                  </Button>
                  <SubmitButton loading={isLoading || loading} />
                </div>
              </div>
              <div className="w-full h-[400px] md:h-[600px] lg:h-[600px] md:col-span-2 pt-3 md:pt-0">
                <AreaDrawingMap
                  onPolygonsChange={setPolygons}
                  onCirclesChange={() => {}}
                  initialPolygons={polygons}
                  initialCircles={[]}
                  toLoad="geofences"
                  editingAreaId={id ? Number(id) : undefined}
                  drawingMode="polygon"
                  showCircleForm={false}
                />
              </div>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default GeofenceForm;
